import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'antd';
import { BiMenu, BiMenuAltLeft } from 'react-icons/bi';

import { useAuth } from '../../hooks/auth';
import Switch from '../../components/SwitchTheme';
import { gameRoute, gameSlugForTipo } from '../../games/core/routing/gameRoute';
import { apiPHP } from '../../services/api';
import { getIsAdmin } from '../../utils/isAdmin';

import { Button, Container, Divider } from './styles';

interface MenuJogos {
  palavrascruzadas: number | null;
  cacapalavras: number | null;
  sudoku: number | null;
  rebus: number | null;
  /** games-rec#68; `null` (ou ausente, back antigo) = botão desabilitado. */
  quiz: number | null;
}

interface MenuGame {
  key: keyof MenuJogos;
  tipo: number;
  label: string;
}

const MENU_GAMES: MenuGame[] = [
  { key: 'cacapalavras', tipo: 7, label: 'Caça palavras' },
  { key: 'palavrascruzadas', tipo: 3, label: 'Palavras Cruzadas' },
  { key: 'sudoku', tipo: 8, label: 'Sudoku' },
  { key: 'rebus', tipo: 5, label: 'Rébus' },
  { key: 'quiz', tipo: 6, label: 'Quiz' },
];

interface HeaderMenuProps {
  children?: ReactNode;
}

export const HeaderMenu = ({ children }: HeaderMenuProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const { pathname } = useLocation();

  // IDs reais por tipo (do backend), p/ os botões linkarem pra jogos que existem
  // em vez de IDs hardcoded. null => sem jogo desse tipo => botão desabilitado.
  const [jogos, setJogos] = useState<MenuJogos>({
    palavrascruzadas: null,
    cacapalavras: null,
    sudoku: null,
    rebus: null,
    quiz: null,
  });

  useEffect(() => {
    setSelected(pathname.substring(1, pathname.length));

    return setSelected(pathname.substring(1, pathname.length));
  }, [pathname]);

  useEffect(() => {
    let cancelled = false; // saiu da tela antes da resposta: não atualiza estado desmontado
    apiPHP
      .get<MenuJogos>('/menu/jogos')
      .then((res) => {
        if (!cancelled) setJogos((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {
        /* API fora / sem jogos — botões ficam desabilitados */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Botão de jogo: rota pelo tipo se houver id; desabilitado se não houver.
  const gameButton = ({ key, tipo, label }: MenuGame) => {
    const id = jogos[key] ?? null;
    const route = id !== null ? gameRoute(tipo, id) : null;
    const active = pathname.startsWith(`/jogo/${gameSlugForTipo(tipo)}/`);
    return route ? (
      <Link key={key} to={route}>
        <Button active={active}>
          <p>{label}</p>
        </Button>
      </Link>
    ) : (
      <Link
        key={key}
        to="/"
        onClick={(event) => event.preventDefault()}
        className="disabled-link"
      >
        <Tooltip placement="right" title="Nenhum jogo cadastrado ainda">
          <Button active={active}>
            <p>{label}</p>
          </Button>
        </Tooltip>
      </Link>
    );
  };

  const admin = getIsAdmin();

  const handleToggleMenu = useCallback(
    (evt) => {
      evt.stopPropagation();
      setIsOpen(!isOpen);
    },
    [isOpen, setIsOpen]
  );

  return (
    <Container isMenuOpen={isOpen} onClick={handleToggleMenu}>
      {children}
      {!isOpen ? <BiMenu /> : <BiMenuAltLeft />}
      <div className="menu">
        <h1>portal de jogos a recreativa</h1>
        {user ? (
          <>
            <Link to="/">
              <Button active={selected === ''}>
                <p>Início</p>
              </Button>
            </Link>
            {admin && (
              <Link to="/admin">
                <Button active={selected === 'admin'}>
                  <p>Criar jogos</p>
                </Button>
              </Link>
            )}
            <Link to="/tournaments">
              <Button active={selected === 'tournaments'}>
                <p>Torneios</p>
              </Button>
            </Link>
            <Link
              to="/leagues"
              onClick={(event) => event.preventDefault()}
              className="disabled-link"
            >
              <Tooltip placement="right" title="Em breve...">
                <Button active={selected === 'leagues'}>
                  <p>Ligas</p>
                </Button>
              </Tooltip>
            </Link>
            <Divider />
            {MENU_GAMES.map(gameButton)}
            <Link
              to="/jogo/wordle"
              onClick={(event) => event.preventDefault()}
              className="disabled-link"
            >
              <Tooltip placement="right" title="Em breve...">
                <Button active={selected === 'jogo/wordle'}>
                  <p>Wordle</p>
                </Button>
              </Tooltip>
            </Link>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Switch />
            </div>
          </>
        ) : (
          <>
            <Link to="/">
              <Button active={selected === ''}>
                <p>login</p>
              </Button>
            </Link>
            <Link to="/cadastrar">
              <Button active={selected === 'cadastrar'}>
                <p>Cadastrar</p>
              </Button>
            </Link>
            <Link to="/recuperar-senha">
              <Button active={selected === 'recuperar-senha'}>
                <p>Recuperar Senha</p>
              </Button>
            </Link>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Switch />
            </div>
          </>
        )}
      </div>
    </Container>
  );
};
